<template>
  <div>
    <div v-if="hasCommandSchema" class="w-100">
      <!-- Navigation controls for examples -->
      <div v-if="maxExampleRows > 0 || currentRowIndex > 0" class="d-flex mt-3 mb-3 align-center justify-center ga-2">
        <v-btn
          icon
          size="small"
          variant="text"
          :disabled="currentRowIndex === 0"
          @click="goToPreviousRow"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="example-counter">
          Example {{ currentRowIndex + 1 }} of {{ Math.max(maxExampleRows, currentRowIndex + 1) }}
        </span>
        <v-btn
          icon
          size="small"
          variant="text"
          :disabled="currentRowIndex >= maxExampleRows"
          @click="goToNextRow"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <!-- Read Models Section -->
      <div v-if="readModels.length > 0" class="mb-4">
        <div class="d-flex mb-2 align-center">
          <v-icon size="small" icon="mdi-magnify" color="green-lighten-1" class="mr-2" />
          <h5 class="read-model-title-compact">
            Input information for {{ commandTitle }}
          </h5>
        </div>
        <div
          v-for="(readModel, readModelIndex) in readModels"
          :key="`read-model-${readModelIndex}`"
          class="read-model-section-compact mb-3"
          :style="{ backgroundColor: readModelCardTypeColor, borderLeftColor: readModelCardTypeBorderColor }"
        >
          <div class="read-model-fields-compact">
            <div class="read-model-entity-name d-flex align-center" :style="{ color: readModelCardTypeBorderColor }">
              <span>
                {{ readModel.entityName }}
                <span v-if="readModel.boundedContextName" class="bounded-context-name">
                  — {{ readModel.boundedContextName }}
                </span>
                <span v-if="readModel.cardinality === 'one-to-many'" class="cardinality-indicator">
                  (List)
                </span>
              </span>
              <v-tooltip v-if="readModel.description" location="top" open-delay="300">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    size="x-small"
                    icon="mdi-information-outline"
                    color="#868E99"
                    class="ml-1 read-model-info-icon"
                  />
                </template>
                <span style="white-space: pre-wrap; max-width: 300px; display: inline-block; word-wrap: break-word;">{{ readModel.description }}</span>
              </v-tooltip>
            </div>
            <div
              v-for="(field, index) in readModel.fields"
              :key="`${readModelIndex}-${index}-read`"
              class="read-model-field-row"
            >
              <span class="read-model-field-name d-flex align-center justify-space-between">
                <span class="d-flex align-center">
                  {{ field.name }}
                  <span
                    v-if="(!field.relatedEntityId || field.fields?.length) && !!field.matchingField?.relatedEntity"
                    class="read-model-field-type"
                  >
                    {{ field.cardinality === 'one-to-many' ? '(Collection)' : '(Object)' }}
                  </span>
                  <v-tooltip v-if="field.description" location="right" open-delay="300">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="x-small"
                        icon="mdi-information-outline"
                        color="#868E99"
                        class="ml-1 comment-info-icon"
                      />
                    </template>
                    <span style="white-space: pre-wrap;">{{ field.description }}</span>
                  </v-tooltip>
                </span>
              </span>
              <span class="read-model-field-value">
                <template
                  v-if="(!field.relatedEntityId || field.fields?.length) && !!field.matchingField && !!field.matchingField.relatedEntity"
                >
                  <template v-if="field.cardinality === 'one-to-many'">
                    <span class="text-grey">Table with {{ field.matchingField.relatedEntity?.fields?.length || 0 }} columns</span>
                  </template>
                  <template v-else>
                    <span>
                      <template v-for="(relatedField, rfIndex) in field.matchingField.relatedEntity?.fields || []" :key="relatedField.name">
                        <span v-if="rfIndex > 0">, </span>
                        <span>{{ relatedField.name }}: {{ relatedField?.exampleData?.[currentRowIndex] }}</span>
                      </template>
                    </span>
                  </template>
                </template>
                <template v-else>
                  <template v-if="hasMultiLineValue(field.exampleData)">
                    <div
                      v-for="(line, lineIndex) in getMultiLineValues(field.exampleData)"
                      :key="lineIndex"
                      class="read-model-multi-line-item"
                    >
                      {{ line }}
                    </div>
                  </template>
                  <span v-else>{{ field.exampleData }}</span>
                </template>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex mb-3 align-center">
        <v-icon size="small" icon="mdi-flash" color="blue-lighten-1" class="mr-2" />
        <h5 class="command-title-compact">
          {{ commandTitle }}<span v-if="boundedContextName" class="bounded-context-name"> — {{ boundedContextName }}</span>
        </h5>
      </div>

      <!-- Command Fields -->
      <div
        v-for="(field, index) in fieldsCommand"
        :key="`${index}-write`"
        class="field-box"
        :style="{ backgroundColor: commandCardTypeColor }"
      >
        <div class="field-box-label d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            {{ field.name }}
            {{
              (!field.relatedEntityId || field.fields?.length) && !!field.matchingField?.relatedEntity ?
                (field.cardinality === 'one-to-many' ? '(Collection)' : '(Object)') : ''
            }}
          </div>
          <span
            v-if="!editingDescription[`write-${index}`]"
            class="add-comment-link"
            @click.stop="startEditingComment(`write-${index}`, rootEntity?.id, field.name)"
          >
            {{ field.description ? 'Edit comment' : '+ Add comment' }}
          </span>
        </div>
        <div
          v-if="!field.relatedEntityId || field.fields?.length"
          class="field-box-content d-flex align-start"
          style="min-height: 1.5em"
        >
          <div v-if="!!field.matchingField && !!field.matchingField.relatedEntity" class="w-100">
            <div v-if="field.cardinality === 'one-to-many'" >
              <div class="embedded-object-table-container mb-1">
                <table class="w-100">
                  <thead>
                    <tr>
                      <th v-for="relatedField in field.matchingField.relatedEntity?.fields || []" :key="relatedField.name">
                        {{ relatedField.name }}
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(data, dataIndex) in field.matchingField.relatedEntity?.fields?.[0]?.exampleData || []"
                      :key="data"
                    >
                      <td v-for="relatedField in field.matchingField.relatedEntity?.fields || []" :key="relatedField.name">
                        {{ relatedField?.exampleData?.[dataIndex] ?? '-' }}
                      </td>
                      <td>
                        <div class="d-flex align-center ga-1">
                          <v-icon size="14" color="grey" icon="mdi-pencil" class="cursor-pointer" />
                          <v-icon size="14" color="grey" icon="mdi-delete" class="cursor-pointer" />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <v-btn
                class="add-item-btn"
                variant="outlined"
                size="small"
                @click="() => {}"
                prepend-icon="mdi-plus-circle-outline"
              >
                Add Item
              </v-btn>
            </div>
            <div v-else>
              <template v-for="relatedField in field.matchingField.relatedEntity?.fields || []" :key="relatedField.name">
                <div class="embedded-object-field d-flex justify-space-between align-center ga-2 py-0.5 px-1.5">
                  <div class="embedded-object-field-label text-truncate">{{ relatedField.name }}:</div>
                  <div
                    class="embedded-object-field-value editable-field"
                    contenteditable="false"
                    :data-entity-id="field.matchingField.relatedEntityId"
                    :data-field-name="relatedField.name"
                    :data-row-index="currentRowIndex"
                    @click="handleFieldClick"
                    @blur="handleFieldBlur"
                    @keydown="handleFieldKeydown"
                    v-html="formatFieldValue(relatedField?.exampleData?.[currentRowIndex])"
                  />
                </div>
              </template>
            </div>
          </div>
          <span
            v-else
            class="editable-field"
            contenteditable="false"
            :data-entity-id="rootEntity?.id"
            :data-field-name="field.name"
            :data-row-index="currentRowIndex"
            @click="handleFieldClick"
            @blur="handleFieldBlur"
            @keydown="handleFieldKeydown"
            v-html="formatFieldValue(field.exampleData)"
          ></span>
        </div>
        <v-menu
          v-model="dropdownMenus[`${index}-${currentRowIndex}`]"
          class="field-options-menu"
          :close-on-content-click="false"
          v-else
        >
          <template v-slot:activator="{ props }">
            <div class="d-flex align-items-center" v-bind="props">
              <div class="field-box-content d-inline-flex align-start flex-grow-1 multi-select-display">
                <template v-if="field.cardinality === 'one-to-many' && field.selectedValues?.length > 0">
                  <div v-for="(value, valIndex) in field.selectedValues" :key="valIndex" class="multi-select-item">
                    <span class="multi-select-bullet">•</span>{{ value }}
                  </div>
                </template>
                <template v-else>
                  {{ field.exampleData }}
                </template>
              </div>
              <v-icon icon="mdi-menu-down" color="#1F2833" />
            </div>
          </template>
          <v-list density="compact">
            <v-list-item
              v-for="(option) in field.options"
              :key="option"
              @click.stop="() => handleDropdownSelect(option, field, `${index}-${currentRowIndex}`)"
            >
              <template v-if="field.cardinality === 'one-to-many'">
                <v-list-item-title class="d-flex align-center ga-2 cursor-pointer">
                  <v-checkbox
                    :model-value="isOptionSelected(option, field)"
                    hide-details
                    density="compact"
                    @click.stop="() => handleDropdownSelect(option, field, `${index}-${currentRowIndex}`)"
                  />
                  {{ option }}
                </v-list-item-title>
              </template>
              <template v-else>
                <v-list-item-title class="d-flex align-center ga-2 cursor-pointer">
                  {{ option }}
                </v-list-item-title>
              </template>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- Comment field for command field -->
        <div v-if="field.description || editingDescription[`write-${index}`]" class="field-comment">
          <div
            class="field-comment-content"
            :class="{ 'editing': editingDescription[`write-${index}`] }"
            contenteditable="false"
            :data-entity-id="rootEntity?.id"
            :data-field-name="field.name"
            :data-field-type="'write'"
            :data-field-index="index"
            @click="handleCommentClick"
            @blur="handleCommentBlur"
            @keydown="handleCommentKeydown"
            v-html="formatFieldValue(field.description)"
          ></div>
        </div>
      </div>
    </div>
    <div v-else class="ma-3">
      {{ event ? 'No Command defined' : 'Select an event to view the command' }}
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { cardHelpers } from '@/mixins/cardHelpers'
import useDomainModel from '@/composables/useDomainModel'
import workflowApi from '@/api/workflowApi'
import { escapeHtml } from '@/utils/sanitize'
import safeStructuredClone from '@/utils/clone'
import { removeHtmlTags } from '@/utils/validate'
import inputHelpers from '@/helpers/inputHelpers'

const { event } = defineProps({ event: Object })

const currentRowIndex = ref(0)
const dropdownMenus = ref({})
const editingDescription = ref({})

const { getMatchingFieldInEntity, cardTypeColor } = useDomainModel()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const rootEntity = computed(() => {
  if (!event) {
    return null
  }
  const entityCard = cardHelpers.methods.getEntityCard(event)

  return entityCard?.schemaId
    ? workflow.value.schemas?.find((entity) => entity.id === entityCard.schemaId)
    : null
})

const toTitleCase = (text) => {
  if (!text) {
    return ''
  }

  let cleanText = removeHtmlTags(text)

  const doc = new DOMParser().parseFromString(cleanText, 'text/html')
  cleanText = doc.body.textContent.trim()

  // Handle snake_case: replace underscores with spaces
  cleanText = cleanText.replace(/_/g, ' ')

  // Handle camelCase: insert space before capital letters (but not at the start)
  cleanText = cleanText.replace(/([a-z])([A-Z])/g, '$1 $2')

  // Capitalize first letter of each word
  return cleanText.split(' ').map((word) => {
    if (word.length === 0) {
      return word
    }

    if (word === word.toUpperCase() && word.match(/^[A-Z0-9<>\-_]+$/)) {
      return word
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }).join(' ')
}

const commandTitle = computed(() => {
  if (!event) {
    return 'Command'
  }
  const commandCard = cardHelpers.methods.getCommandCard(event)
  if (!commandCard?.description) {
    return 'Command'
  }

  return toTitleCase(commandCard.description)
})

const boundedContextName = computed(() => {
  if (!rootEntity.value?.boundedContext) {
    return null
  }

  const boundedContext = workflow.value.boundedContexts?.find(
    (bc) => bc.id === rootEntity.value.boundedContext
  )

  return boundedContext?.name || null
})

const commandCardTypeColor = computed(() => {
  if (!event) {
    return 'rgba(33, 150, 243, 0.12)' // Default blue with transparency
  }
  const commandCard = cardHelpers.methods.getCommandCard(event)
  if (!commandCard?.cardType) {
    return 'rgba(33, 150, 243, 0.12)' // Default blue with transparency
  }
  const color = cardTypeColor(commandCard.cardType)
  if (!color || color === '#eeeeee') {
    return 'rgba(33, 150, 243, 0.12)' // Default blue with transparency
  }

  // Convert rgb color to rgba with transparency
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `rgba(${r}, ${g}, ${b}, 0.12)`
  }
  return 'rgba(33, 150, 243, 0.12)'
})

const readModelCardTypeColor = computed(() => {
  if (!event) {
    return 'rgba(76, 175, 80, 0.12)' // Default green with transparency
  }
  const queryCards = cardHelpers.methods.getQueryCards(event)
  const firstQueryCard = queryCards?.[0]
  if (!firstQueryCard?.cardType) {
    return 'rgba(76, 175, 80, 0.12)' // Default green with transparency
  }
  const color = cardTypeColor(firstQueryCard.cardType)
  if (!color || color === '#eeeeee') {
    return 'rgba(76, 175, 80, 0.12)' // Default green with transparency
  }

  // Convert rgb color to rgba with transparency
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `rgba(${r}, ${g}, ${b}, 0.12)`
  }
  return 'rgba(76, 175, 80, 0.12)'
})

const readModelCardTypeBorderColor = computed(() => {
  if (!event) {
    return '#868E99' // Default grey
  }
  const queryCards = cardHelpers.methods.getQueryCards(event)
  const firstQueryCard = queryCards?.[0]
  if (!firstQueryCard?.cardType) {
    return '#868E99' // Default grey
  }
  const color = cardTypeColor(firstQueryCard.cardType)
  if (!color || color === '#eeeeee') {
    return '#868E99' // Default grey
  }
  return color
})

const hasCommandSchema = computed(() => {
  if (!event) {
    return false
  }
  const commandCard = cardHelpers.methods.getCommandCard(event)

  return !!commandCard?.schemaId
})

const fieldsCommand = computed(() => {
  if (!event) {
    return []
  }
  const commandCard = cardHelpers.methods.getCommandCard(event)
  const commandSchema = commandCard?.schemaId
    ? workflow.value.schemas?.find((schema) => schema.id === commandCard.schemaId)
    : null

  if (!commandSchema) {
    return []
  }

  const filteredFields = commandSchema.fields.filter((field) => !field.hideInForm)

  return filteredFields.map((field) => {
    const matchingField = getMatchingFieldInEntity(rootEntity.value, field.name)
    const relatedEntity = matchingField?.relatedEntityId
      ? workflow.value.schemas?.find((entity) => entity.id === matchingField.relatedEntityId)
      : null

    let exampleData = ''
    const options = []

    if (matchingField) {
      if (matchingField.relatedEntityId && relatedEntity) {
        // For reference fields, use the value from rootEntity
        exampleData = matchingField.exampleData?.[currentRowIndex.value] || ''

        // Build options for the dropdown menu from related entity (limit to first 7 fields)
        relatedEntity.fields.slice(0, 7).forEach((field) => {
          (field.exampleData || []).forEach((item, dataIndex) => {
            if (!options[dataIndex]) {
              options[dataIndex] = item || ''
            } else {
              options[dataIndex] += item ? ` — ${item}` : ''
            }
          })
        })
      } else {
        exampleData = matchingField.exampleData?.[currentRowIndex.value] || ''
      }
    }

    const subfieldNames = (field.fields ?? []).map((subfield) => subfield.name)
    const matchingFieldRelatedEntityFields = matchingField?.relatedEntity?.fields?.filter((item) =>
      subfieldNames.includes(item.name)
    )

    // For one-to-many fields, parse selected values from exampleData (split by newlines)
    const selectedValues = exampleData && field?.cardinality === 'one-to-many'
      ? exampleData.split('\n').filter((val) => val.trim())
      : []

    return {
      ...field,
      description: matchingField?.description || field.description || '',
      cardinality: field?.cardinality || 'one-to-one',
      matchingField: matchingField && matchingField.relatedEntity
        ? { ...matchingField, relatedEntity: { ...matchingField.relatedEntity, fields: matchingFieldRelatedEntityFields } }
        : null,
      dataType: matchingField?.dataType,
      options: options.filter(Boolean),
      exampleData,
      selectedValues // Store parsed selected values for multi-select
    }
  })
})

const readModels = computed(() => {
  if (!event) {
    return []
  }

  const queryCards = cardHelpers.methods.getQueryCards(event)
  const readModelsArray = []

  queryCards.forEach((queryCard) => {
    const readModelSchema = queryCard.schemaId
      ? workflow.value.schemas?.find((schema) => schema.id === queryCard.schemaId)
      : null

    if (!readModelSchema) {
      return
    }

    // Get the entity for this read model (from the Query schema's entityId)
    const readModelEntity = readModelSchema?.entityId
      ? workflow.value.schemas?.find((entity) => entity.id === readModelSchema.entityId)
      : null

    // Only show if connected to an entity
    if (!readModelEntity) {
      return
    }

    const filteredFields = readModelSchema.fields.filter((field) => !field.hideInForm)

    const fields = filteredFields.map((field) => {
      const matchingField = getMatchingFieldInEntity(readModelEntity, field.name)
      const relatedEntity = matchingField?.relatedEntityId
        ? workflow.value.schemas?.find((entity) => entity.id === matchingField.relatedEntityId)
        : null

      let exampleData = ''
      const options = []

      if (matchingField) {
        if (matchingField.relatedEntityId && relatedEntity) {
          exampleData = matchingField.exampleData?.[currentRowIndex.value] || ''

          relatedEntity.fields.slice(0, 7).forEach((field) => {
            (field.exampleData || []).forEach((item, dataIndex) => {
              if (!options[dataIndex]) {
                options[dataIndex] = item || ''
              } else {
                options[dataIndex] += item ? ` — ${item}` : ''
              }
            })
          })
        } else {
          exampleData = matchingField.exampleData?.[currentRowIndex.value] || ''
        }
      }

      const subfieldNames = (field.fields ?? []).map((subfield) => subfield.name)
      const matchingFieldRelatedEntityFields = matchingField?.relatedEntity?.fields?.filter((item) =>
        subfieldNames.includes(item.name)
      )

      return {
        ...field,
        description: matchingField?.description || field.description || '',
        cardinality: field?.cardinality || 'one-to-one',
        matchingField: matchingField && matchingField.relatedEntity
          ? { ...matchingField, relatedEntity: { ...matchingField.relatedEntity, fields: matchingFieldRelatedEntityFields } }
          : null,
        dataType: matchingField?.dataType,
        options: options.filter(Boolean),
        exampleData
      }
    })

    if (fields.length > 0) {
      // Find bounded context for this read model entity
      const boundedContext = readModelEntity?.boundedContext
        ? workflow.value.boundedContexts?.find((bc) => bc.id === readModelEntity.boundedContext)
        : null

      readModelsArray.push({
        description: queryCard.description?.replace(/<\/?p>/g, '') || '',
        entityId: readModelSchema?.entityId,
        entityName: readModelEntity.name,
        boundedContextName: boundedContext?.name || null,
        cardinality: readModelSchema?.cardinality || 'one-to-one',
        fields
      })
    }
  })

  return readModelsArray
})

const maxExampleRows = computed(() => {
  if (!event) {
    return 0
  }
  let maxRows = 0

  const commandCard = cardHelpers.methods.getCommandCard(event)
  const commandSchema = commandCard?.schemaId
    ? workflow.value.schemas?.find((schema) => schema.id === commandCard.schemaId)
    : null

  if (!commandSchema) {
    return 0
  }

  const filteredFields = commandSchema.fields.filter((field) => !field.hideInForm)

  filteredFields.forEach((field) => {
    const matchingField = getMatchingFieldInEntity(rootEntity.value, field.name)

    if (matchingField) {
      // Check direct field exampleData
      if (matchingField.exampleData?.length) {
        maxRows = Math.max(maxRows, matchingField.exampleData.length)
      }
    }
  })

  // Also check read models for max rows
  readModels.value.forEach((readModel) => {
    readModel.fields.forEach((field) => {
      if (field.matchingField?.exampleData?.length) {
        maxRows = Math.max(maxRows, field.matchingField.exampleData.length)
      }
      if (field.matchingField?.relatedEntity?.fields) {
        field.matchingField.relatedEntity.fields.forEach((relatedField) => {
          if (relatedField.exampleData?.length) {
            maxRows = Math.max(maxRows, relatedField.exampleData.length)
          }
        })
      }
    })
  })

  return maxRows
})

const goToPreviousRow = () => {
  if (currentRowIndex.value > 0) {
    currentRowIndex.value--
  }
}

const goToNextRow = () => {
  // Allow navigating one row beyond existing data (to create new row)
  if (currentRowIndex.value < maxExampleRows.value) {
    currentRowIndex.value++
  }
}

// Preserve row index when event changes, but don't pre-create rows
watch(() => event?.id, async () => {
  // If event is undefined/null, just return
  if (!event) {
    return
  }

  // Wait for next tick to ensure rootEntity and computed values are updated
  await nextTick()

  if (!rootEntity.value) {
    return
  }

  // Cap the row index if it exceeds available rows (allow one beyond for creating new row)
  const maxRows = maxExampleRows.value
  // Allow staying at maxRows (one beyond existing) to create new row, but cap beyond that
  if (currentRowIndex.value > maxRows) {
    currentRowIndex.value = Math.max(0, maxRows)
  }
})

const handleFieldClick = (event) => {
  if (store.state.isWorkflowDisabled) {
    return
  }
  const contenteditable = event.target.getAttribute('contenteditable')
  if (contenteditable !== 'true') {
    // Save original value for escape key (get text version with newlines)
    const textValue = event.target.innerText
    event.target.setAttribute('data-original-value', textValue)
    // Set the text content for editing (browser will preserve line breaks with white-space: pre-wrap)
    event.target.innerText = textValue
    event.target.setAttribute('contenteditable', 'true')
    event.target.focus()
    inputHelpers.moveCursorToTextEnd(event.target)
  }
}

const handleFieldBlur = async (event) => {
  const target = event.target
  if (target.innerText.length > 256) {
    alert('Data field must be max 256 characters')
    const originalValue = target.getAttribute('data-original-value')
    target.innerText = originalValue
    target.setAttribute('contenteditable', 'false')
    return
  }

  target.setAttribute('contenteditable', 'false')

  const entityId = target.getAttribute('data-entity-id')
  const fieldName = target.getAttribute('data-field-name')
  const rowIndex = parseInt(target.getAttribute('data-row-index'))

  if (!entityId || !fieldName) {
    return
  }

  const newValue = target.innerText.trim()
  const entityIndex = workflow.value.schemas.findIndex((e) => e.id === entityId)

  if (entityIndex === -1) {
    return
  }

  const entity = workflow.value.schemas[entityIndex]
  const fieldIndex = entity.fields.findIndex((f) => f.name === fieldName)

  if (fieldIndex === -1) {
    return
  }

  const field = entity.fields[fieldIndex]
  const oldValue = field.exampleData?.[rowIndex] || ''

  if (oldValue === newValue) {
    return
  }

  // Clone first to avoid mutating the store
  const newSchemas = safeStructuredClone(workflow.value.schemas)
  const clonedEntity = newSchemas[entityIndex]
  const clonedField = clonedEntity.fields[fieldIndex]

  // Ensure exampleData array exists and has enough elements in the cloned data
  if (!clonedField.exampleData) {
    clonedField.exampleData = []
  }
  while (clonedField.exampleData.length <= rowIndex) {
    clonedField.exampleData.push('')
  }

  clonedField.exampleData[rowIndex] = newValue

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })

  // Remove the data-original-value attribute after successful save
  target.removeAttribute('data-original-value')
}

const isOptionSelected = (option, field) => {
  if (field.cardinality !== 'one-to-many') {
    return false
  }
  const firstValue = option.split(' — ')[0].trim()
  return field.selectedValues?.includes(firstValue) || false
}

const handleDropdownSelect = async (option, field, menuKey) => {
  if (!field || !field.name || !rootEntity.value) {
    return
  }

  // Extract the first value from the option (before " — ")
  const firstValue = option.split(' — ')[0].trim()

  if (!firstValue) {
    return
  }

  const entityId = rootEntity.value.id
  const fieldName = field.name
  const rowIndex = currentRowIndex.value

  const entityIndex = workflow.value.schemas.findIndex((e) => e.id === entityId)

  if (entityIndex === -1) {
    return
  }

  const entity = workflow.value.schemas[entityIndex]
  const fieldIndex = entity.fields.findIndex((f) => f.name === fieldName)

  if (fieldIndex === -1) {
    return
  }

  const entityField = entity.fields[fieldIndex]
  const currentValue = entityField.exampleData?.[rowIndex] || ''

  // Clone first to avoid mutating the store
  const newSchemas = safeStructuredClone(workflow.value.schemas)
  const clonedEntity = newSchemas[entityIndex]
  const clonedField = clonedEntity.fields[fieldIndex]

  // Ensure exampleData array exists and has enough elements in the cloned data
  if (!clonedField.exampleData) {
    clonedField.exampleData = []
  }
  while (clonedField.exampleData.length <= rowIndex) {
    clonedField.exampleData.push('')
  }

  let newValue = firstValue

  // For one-to-many fields, toggle selection (multi-select)
  if (field.cardinality === 'one-to-many') {
    const selectedValues = currentValue ? currentValue.split('\n').filter((val) => val.trim()) : []
    const isSelected = selectedValues.includes(firstValue)

    if (isSelected) {
      // Remove from selection
      selectedValues.splice(selectedValues.indexOf(firstValue), 1)
      newValue = selectedValues.join('\n')
    } else {
      // Add to selection
      selectedValues.push(firstValue)
      newValue = selectedValues.join('\n')
    }
  }

  // Only update if value changed
  if (currentValue === newValue) {
    // Don't close menu for multi-select to allow multiple selections
    if (field.cardinality !== 'one-to-many' && menuKey) {
      dropdownMenus.value[menuKey] = false
    }
    return
  }

  clonedField.exampleData[rowIndex] = newValue

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })

  // Close the menu after selection (only for single-select)
  if (field.cardinality !== 'one-to-many' && menuKey) {
    dropdownMenus.value[menuKey] = false
  }
}

const handleFieldKeydown = (event) => {
  const target = event.target
  const isContentEditable = target.getAttribute('contenteditable') === 'true'

  switch (event.keyCode) {
    case 13: // enter
      if (isContentEditable) {
        // Check for Alt/Option key (on Mac, Option key reports as altKey)
        if (event.altKey) {
          // Alt+Enter (Option+Enter on Mac) = insert newline
          event.preventDefault()
          event.stopPropagation()
          // Insert 2 br tags if none exist, otherwise insert 1
          const existingBrs = target.querySelectorAll('br')
          const shouldInsertTwo = existingBrs.length === 0

          const selection = window.getSelection()
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            range.deleteContents()

            if (shouldInsertTwo) {
              const br1 = document.createElement('br')
              const br2 = document.createElement('br')
              range.insertNode(br1)
              range.setStartAfter(br1)
              range.collapse(true)
              range.insertNode(br2)
              range.setStartAfter(br2)
              range.collapse(true)
            } else {
              const br = document.createElement('br')
              range.insertNode(br)
              range.setStartAfter(br)
              range.collapse(true)
            }

            selection.removeAllRanges()
            selection.addRange(range)
          }
          return
        } else {
          // Regular Enter = blur (save and exit)
          target.blur()
          event.preventDefault()
        }
      }
      break
    case 27: // escape
      const originalValue = target.getAttribute('data-original-value') || target.textContent
      target.innerText = originalValue
      target.setAttribute('contenteditable', 'false')
      event.preventDefault()
      break
    default:
      if (!store.state.isWorkflowDisabled && !isContentEditable && isPrintableCharacter(event.keyCode) && !event.altKey && !event.metaKey) {
        target.setAttribute('contenteditable', 'true')
        target.setAttribute('data-original-value', target.innerText)
        target.innerText = ''
        target.focus()
        inputHelpers.moveCursorToTextEnd(target)
        event.preventDefault()
      }
  }
  event.stopPropagation()
}

const formatFieldValue = (value) => {
  if (!value) {
    return ''
  }
  return escapeHtml(String(value)).replace(/\n/g, '<br>')
}

const hasMultiLineValue = (value) => {
  if (!value) {
    return false
  }
  return value.includes('\n')
}

const getMultiLineValues = (value) => {
  if (!value) {
    return []
  }
  const lines = value.split('\n').filter((line) => line.trim())
  return lines.length > 0 ? lines : ['']
}

const isPrintableCharacter = (keycode) => {
  const printable =
    (keycode > 47 && keycode < 58) || // number keys
    keycode === 32 || keycode === 13 || // spacebar & return key(s)
    (keycode > 64 && keycode < 91) || // letter keys
    (keycode > 95 && keycode < 112) || // numpad keys
    (keycode > 185 && keycode < 193) || // ;=,-./` (in order)
    (keycode > 218 && keycode < 223) // [\]' (in order)
  return printable
}

const startEditingComment = (key, entityId, fieldName, readModelIndex = null, fieldIndex = null) => {
  if (store.state.isWorkflowDisabled) {
    return
  }
  editingDescription.value[key] = true
  // Wait for next tick to ensure DOM is updated
  nextTick(() => {
    const selector = readModelIndex !== null
      ? `[data-field-type="read"][data-entity-id="${entityId}"][data-field-name="${fieldName}"][data-read-model-index="${readModelIndex}"][data-field-index="${fieldIndex}"]`
      : `[data-field-type="write"][data-entity-id="${entityId}"][data-field-name="${fieldName}"]`
    const target = document.querySelector(selector)
    if (target) {
      const textValue = target.innerText || ''
      target.setAttribute('data-original-value', textValue)
      target.innerText = textValue
      target.setAttribute('contenteditable', 'true')
      target.focus()
      inputHelpers.moveCursorToTextEnd(target)
    }
  })
}

const handleCommentClick = (event) => {
  if (store.state.isWorkflowDisabled) {
    return
  }
  const target = event.target
  const contenteditable = target.getAttribute('contenteditable')
  if (contenteditable !== 'true') {
    const key = target.getAttribute('data-field-type') === 'read'
      ? `read-${target.getAttribute('data-read-model-index')}-${target.getAttribute('data-field-index')}`
      : `write-${target.getAttribute('data-field-index')}`
    editingDescription.value[key] = true
    const textValue = target.innerText || ''
    target.setAttribute('data-original-value', textValue)
    target.innerText = textValue
    target.setAttribute('contenteditable', 'true')
    target.focus()
    inputHelpers.moveCursorToTextEnd(target)
  }
}

const handleCommentBlur = async (event) => {
  const target = event.target
  const newValue = target.innerText.trim()

  target.setAttribute('contenteditable', 'false')

  const entityId = target.getAttribute('data-entity-id')
  const fieldName = target.getAttribute('data-field-name')
  const fieldType = target.getAttribute('data-field-type')
  const key = fieldType === 'read'
    ? `read-${target.getAttribute('data-read-model-index')}-${target.getAttribute('data-field-index')}`
    : `write-${target.getAttribute('data-field-index')}`

  editingDescription.value[key] = false

  if (!entityId || !fieldName) {
    return
  }

  const entityIndex = workflow.value.schemas.findIndex((e) => e.id === entityId)

  if (entityIndex === -1) {
    return
  }

  const entity = workflow.value.schemas[entityIndex]
  const fieldIndex = entity.fields.findIndex((f) => f.name === fieldName)

  if (fieldIndex === -1) {
    return
  }

  const field = entity.fields[fieldIndex]
  const oldValue = field.description || ''

  if (oldValue === newValue) {
    target.removeAttribute('data-original-value')
    return
  }

  // Clone first to avoid mutating the store
  const newSchemas = safeStructuredClone(workflow.value.schemas)
  const clonedEntity = newSchemas[entityIndex]
  const clonedField = clonedEntity.fields[fieldIndex]

  // Set to empty string if cleared, which will hide the field
  clonedField.description = newValue || ''

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })

  target.removeAttribute('data-original-value')
}

const handleCommentKeydown = (event) => {
  const target = event.target
  const isContentEditable = target.getAttribute('contenteditable') === 'true'

  switch (event.keyCode) {
    case 13: // enter
      if (isContentEditable) {
        // Check for Alt/Option key (on Mac, Option key reports as altKey)
        if (event.altKey) {
          // Alt+Enter (Option+Enter on Mac) = insert newline
          event.preventDefault()
          event.stopPropagation()
          // Insert 2 br tags if none exist, otherwise insert 1
          const existingBrs = target.querySelectorAll('br')
          const shouldInsertTwo = existingBrs.length === 0

          const selection = window.getSelection()
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            range.deleteContents()

            if (shouldInsertTwo) {
              const br1 = document.createElement('br')
              const br2 = document.createElement('br')
              range.insertNode(br1)
              range.setStartAfter(br1)
              range.collapse(true)
              range.insertNode(br2)
              range.setStartAfter(br2)
              range.collapse(true)
            } else {
              const br = document.createElement('br')
              range.insertNode(br)
              range.setStartAfter(br)
              range.collapse(true)
            }

            selection.removeAllRanges()
            selection.addRange(range)
          }
          return
        } else {
          // Regular Enter = blur (save and exit)
          target.blur()
          event.preventDefault()
        }
      }
      break
    case 27: // escape
      const originalValue = target.getAttribute('data-original-value') || ''
      target.innerText = originalValue
      target.setAttribute('contenteditable', 'false')
      const fieldType = target.getAttribute('data-field-type')
      const key = fieldType === 'read'
        ? `read-${target.getAttribute('data-read-model-index')}-${target.getAttribute('data-field-index')}`
        : `write-${target.getAttribute('data-field-index')}`
      editingDescription.value[key] = false
      event.preventDefault()
      break
  }
  event.stopPropagation()
}
</script>

<style scoped>
.example-counter {
  font-size: 12px;
  color: #1F2833;
  white-space: nowrap;
}

.read-models-toggle {
  margin-left: 0;
}

.command-title-compact {
  font-size: 13px;
  font-weight: 500;
  color: #4F5359;
  margin: 0;
}

.bounded-context-name {
  color: #868E99;
  font-weight: 400;
}

.read-model-title-compact {
  font-size: 13px;
  font-weight: 500;
  color: #4F5359;
  margin: 0;
}

.read-model-section-compact {
  background: var(--light-1);
  border-left: 3px solid #868E99;
  padding: 8px 12px;
  border-radius: 4px;
}

.read-model-description-compact {
  font-size: 12px;
  color: #4F5359;
  font-weight: 500;
  margin-bottom: 6px;
}

.read-model-entity-name {
  font-size: 11px;
  font-weight: 600;
}

.cardinality-indicator {
  font-size: 10px;
  font-weight: 400;
  color: #868E99;
  margin-left: 4px;
}

.read-model-fields-compact {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.read-model-field-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 12px;
  line-height: 1.4;
  gap: 8px;
}

.read-model-field-name {
  color: #4F5359;
  font-weight: 400;
  flex-shrink: 0;
  min-width: 100px;
}

.read-model-field-type {
  font-weight: 400;
  color: #868E99;
  font-size: 11px;
}

.read-model-field-value {
  color: #1F2833;
  font-weight: 500;
  text-align: right;
  flex-grow: 1;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;

  > span {
    text-align: right;
  }
}

.read-model-multi-line-item {
  text-align: right;
  line-height: 1.4;
}

.field-box {
  padding: 6px 12px;
  border: 1px solid #868E99;
  border-radius: 10px;
  margin-bottom: 14px;

  .field-box-label {
    font-size: 12px;
    color: grey;
    margin-bottom: 3px;
    line-height: 1.3;
  }
}

:deep(.v-checkbox) {
  .v-input__control {
    .v-selection-control {
      min-height: 0;

      .v-selection-control__wrapper {
        width: 21px;
        height: 21px;
      }
    }
  }
}

.field-options-menu {
  .v-overlay__content {
    .v-list-item {
      &:hover {
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
    }
  }
}

.embedded-object-field {
  border-radius: 6px;

  .embedded-object-field-label {
    width: 80px;
    font-size: 12px;
    color: #1F2833;
  }

  .embedded-object-field-value {
    flex-grow: 1;
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }
}

.editable-field {
  cursor: text;
  min-height: 1.5em;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 4px 4px;
  display: inline-block;
  width: 100%;
  background: transparent;
  border: 1px solid transparent;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }

  &[contenteditable="true"] {
    background-color: var(--light-1);
    border: 1px solid #868E99;
    border-radius: 2px;
    padding: 4px 4px;
    outline: none;
  }

  &:empty::before {
    content: '\200b'; /* Zero-width space to maintain height when empty */
    display: inline-block;
  }

  &:empty {
    min-height: 1.5em;
  }
}

.embedded-object-table-container {
  overflow: auto;
  background: var(--light-1);
  border-radius: 6px;

  table {
    border-collapse: collapse;

    tr {
      th {
        height: auto !important;
        color: #8f8f8f;
        padding: 6px 6px 0 6px;
        text-align: left;
        font-size: 12px;
        font-weight: 400;
        border-bottom: 1px solid #d5d5d5;
        white-space: nowrap;
      }

      td {
        padding: 6px;
        font-size: 12px;
        font-weight: 400;
        height: auto !important;
        white-space: nowrap;

        &:focus-within, &:focus {
          outline: none;
        }
      }
    }
  }
}

:deep(.add-item-btn) {
  &.v-btn.v-btn--size-small.v-btn--variant-outlined {
    padding: 2px 8px 2px 4px !important;
    height: 24px !important;

    .v-btn__content {
      font-size: 12px;
    }

    .v-btn__prepend {
      margin-inline: 0;

      .v-icon {
        font-size: 15px;
      }
    }
  }
}

.multi-select-display {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
  justify-content: flex-start;
}

.multi-select-item {
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
}

.multi-select-bullet {
  color: #868E99;
  font-weight: bold;
  margin-right: 2px;
}

.field-comment {
  margin-top: 4px;
  margin-left: 4px;
}

.field-comment-content {
  font-size: 11px;
  color: #868E99;
  font-style: italic;
  line-height: 1.4;
  cursor: text;
  min-height: 1.2em;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 4px 6px;
  border-radius: 3px;
  border: 1px solid transparent;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  background: transparent;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    color: #4F5359;
  }

  &[contenteditable="true"] {
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid #868E99;
    border-radius: 3px;
    padding: 4px 6px;
    outline: none;
    color: #1F2833;
    font-style: normal;
    min-height: 1.5em;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:empty:not([contenteditable="true"])::before {
    content: 'Add comment...';
    opacity: 0.5;
  }

  &:empty[contenteditable="true"]::before {
    content: '';
  }
}

.read-model-field-row + .field-comment {
  margin-top: 2px;
  margin-left: 0;
}

.add-comment-link {
  font-size: 10px;
  color: #868E99;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, color 0.2s;
  white-space: nowrap;
  margin-left: 8px;

  &:hover {
    color: #4F5359;
  }
}

.field-box:hover .add-comment-link,
.read-model-field-row:hover .add-comment-link {
  opacity: 1;
}

.comment-info-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.read-model-info-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.field-box-label {
  position: relative;
}

[data-theme="dark"] {
  .example-counter,
  .read-model-field-value,
  .embedded-object-field-label {
    color: var(--text-dark);
  }

  .command-title-compact,
  .read-model-title-compact,
  .read-model-description-compact,
  .read-model-field-name {
    color: var(--dark-4);
  }

  .bounded-context-name,
  .cardinality-indicator,
  .read-model-field-type,
  .multi-select-bullet,
  .field-comment-content,
  .add-comment-link {
    color: var(--dark-2);
  }

  .field-box-label {
    color: var(--dark-2);
  }

  .field-comment-content:hover,
  .add-comment-link:hover {
    color: var(--dark-4);
  }

  .field-comment-content[contenteditable="true"] {
    background-color: var(--light-2);
    color: var(--text-dark);
    border-color: var(--dark-2);
  }

  .editable-field:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
}
</style>
